import { AuditOutlined, CrownFilled, DeleteFilled, MergeCellsOutlined, MergeOutlined, ProfileTwoTone, StarFilled, StarOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Empty, Radio, Tag, Tooltip } from "antd";
import mergeClassNames from "common/mergeClassNames";
import BadgeIndicator, { IBadgeIndicatorColor } from "components/atoms/BadgeIndicator";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import GoldenNominalBadge from "components/atoms/GoldenNominalBadge";
import DataObjectFieldsPreview from "components/organisms/DataObjectFieldsPreview";
import StewardResolveModal from "components/molecules/StewardResolveModal";
import SuridDetailModal from "components/molecules/SuridDetailModal";
import { isDatasetGolden } from "common/goldenNominal";
import { useAppSelector } from "hooks";
import React from "react";

interface IDuplicateDataObjectComparisonProps {
    dataTags?: string[];
    dataObjectList: IDataObjectRecord[];
    onNominateSurvivor: (obj: IDataObjectRecord, type: "MERGE_DUPLICATES" | "DELETE_DUPLICATES") => void;
    onRemoveSurvivorNomination: (obj: IDataObjectRecord) => void;
    onPreviewClick: (obj: IDataObjectRecord) => void;
    onMergeRulesClick: (obj: IDataObjectRecord, group: IDataObjectRecord[]) => void;
}

function GridHeading({ children }: { children: React.ReactNode }) {
    return <div className="col-span-full border-l border-r border-dashed border-gray-200 bg-slate-100 px-4 py-1 text-[10px] font-bold">{children}</div>;
}

function GridCell({
    children,
    className,
    title,
    type,
    index,
    gold,
}: {
    children: React.ReactNode;
    className?: string;
    title?: string;
    type?: "MERGE" | "DELETE";
    index?: number;
    gold?: boolean;
}) {
    return (
        <div
            className={mergeClassNames(
                "border-r border-dashed border-gray-200 px-4 py-2",
                index === 0 ? "border-l" : "",
                type === "MERGE" ? "bg-green-50" : type === "DELETE" ? "bg-red-50" : gold ? "bg-amber-50" : "",
                className,
            )}
            title={title}
        >
            {children}
        </div>
    );
}

function GridBadge({ type, isSurvivor, isAuto }: { type?: "MERGE" | "DELETE"; isSurvivor?: boolean; isAuto?: boolean }) {
    let color: IBadgeIndicatorColor = "blue";
    let badgeText = "No Action";

    if (isSurvivor) {
        color = "yellow";
        // Distinguish the golden-nominal default from a human choice — a steward can see at a
        // glance whether anyone actually reviewed this cluster.
        badgeText = isAuto ? "Survivor (Auto)" : "Survivor";
    }

    if (type === "MERGE") {
        color = "green";
        badgeText = "To Merge";
    }

    if (type === "DELETE") {
        color = "red";
        badgeText = "To Delete";
    }

    return <BadgeIndicator color={color}>{badgeText}</BadgeIndicator>;
}

export default function DuplicateDataObjectComparison({
    dataObjectList,
    dataTags = [],
    onNominateSurvivor,
    onRemoveSurvivorNomination,
    onPreviewClick,
    onMergeRulesClick,
}: IDuplicateDataObjectComparisonProps) {
    const gridTemplateColumns = `repeat(${dataObjectList.length}, 1fr)`;
    const [alignedGrid, setAlignedGrid] = React.useState<React.ReactNode[]>([]);
    const [tab, setTab] = React.useState<"all" | "merge" | "delete" | "none">("all");

    const goldenBySchema = useAppSelector((s) => s.goldenNominal?.goldenBySchema);
    // A record is golden when it belongs to its schema's golden nominal dataset — from the
    // backend-stamped _isGoldenNominal, combined with any in-session nominate/revoke override.
    const isGold = React.useCallback(
        (d: IDataObjectRecord) => isDatasetGolden(goldenBySchema, d._schemaId, d._dataSetId, d._isGoldenNominal),
        [goldenBySchema],
    );
    // Does this cluster contain a record from the golden nominal dataset? If so, that
    // record is the default survivor and the rest are subordinate.
    const hasGolden = React.useMemo(() => dataObjectList.some((d) => isGold(d)), [dataObjectList, isGold]);

    // Stewardship (PRE-2287): the survivor flagged because the merge left unresolved conflicts.
    const stewardFlagged = React.useMemo(() => dataObjectList.find((d) => d.__needsStewardReview), [dataObjectList]);
    const [resolveOpen, setResolveOpen] = React.useState(false);
    // Cleared-in-session marker: the store's cluster records are not refetched on resolve,
    // so hide the banner locally once THIS cluster's conflicts were resolved.
    const [resolvedLocally, setResolvedLocally] = React.useState<string | null>(null);
    const stewardBannerVisible = stewardFlagged && resolvedLocally !== stewardFlagged._dataObjectId;
    // PRE-2378 — the cluster's golden record identity (stamped at merge execution).
    const [suridOpen, setSuridOpen] = React.useState<string | null>(null);

    const mergeCount = React.useMemo(() => dataObjectList.filter((d) => d._mergeDuplicates).length, [dataObjectList]);
    const deleteCount = React.useMemo(() => dataObjectList.filter((d) => d._deleteDuplicates).length, [dataObjectList]);

    const inProgress = React.useMemo(
        () => dataObjectList.some((data) => data._nominationResult === "REMOVE_IN_PROGRESS" || data._nominationResult === "IN_PROGRESS"),
        [dataObjectList],
    );

    React.useEffect(() => {
        const survivorOptions: React.ReactNode[] = [];
        const recordIds: React.ReactNode[] = [];
        const businessAreaNames: React.ReactNode[] = [];
        const schemaNames: React.ReactNode[] = [];
        const dataSetNames: React.ReactNode[] = [];
        const recordFields: React.ReactNode[] = [];
        // Loop all tags and place them into respective rows
        const taggings: Record<string, React.ReactNode[]> = {};
        dataTags.forEach((t) => (taggings[t] = [])); // Initialise

        for (let i = 0; i < dataObjectList.length; i++) {
            // Filter by tab
            if (tab === "merge" && !dataObjectList[i]._mergeDuplicates) {
                continue;
            }
            if (tab === "delete" && !dataObjectList[i]._deleteDuplicates) {
                continue;
            }
            if (tab === "none" && dataObjectList[i]._survivor) {
                continue;
            }

            const actionType: "MERGE" | "DELETE" | undefined = dataObjectList[i]._mergeDuplicates ? "MERGE" : dataObjectList[i]._deleteDuplicates ? "DELETE" : undefined;
            const gold = isGold(dataObjectList[i]);

            survivorOptions.push(
                <GridCell key={`survivor-${i}`} index={i} className="flex flex-wrap items-center gap-2 border-t" type={actionType} gold={gold}>
                    {hasGolden &&
                        (gold ? (
                            <GoldenNominalBadge label="Golden Nominal — Survives" />
                        ) : (
                            <GoldenNominalBadge tone="subordinate" label="Subordinate" />
                        ))}
                    <GridBadge type={actionType} isSurvivor={dataObjectList[i]._survivor} isAuto={dataObjectList[i]._survivorAuto} />
                    {dataObjectList[i]._surId && (
                        <Tooltip title={`Golden record id ${dataObjectList[i]._surId} — click for XREFs & history`}>
                            <Tag
                                color="purple"
                                className="m-0 cursor-pointer"
                                onClick={() => setSuridOpen(dataObjectList[i]._surId as string)}
                                data-testid="surid-chip"
                            >
                                SURID…{String(dataObjectList[i]._surId).slice(-6)}
                            </Tag>
                        </Tooltip>
                    )}
                    {!dataObjectList[i]._deleteDuplicates && !dataObjectList[i]._mergeDuplicates && (
                        <>
                            {dataObjectList[i]._deduplicationOption !== "DELETE_DUPLICATES" && (
                                <Tooltip placement="bottomLeft" title={dataObjectList[i]._survivor ? "Click to remove merge" : "Nominate to merge"}>
                                    {dataObjectList[i]._survivor ? (
                                        <Button
                                            icon={<StarFilled />}
                                            size="small"
                                            type="primary"
                                            style={{ background: "green" }}
                                            loading={inProgress}
                                            onClick={() => onRemoveSurvivorNomination(dataObjectList[i])}
                                        />
                                    ) : (
                                        <Button
                                            icon={<StarOutlined />}
                                            size="small"
                                            type="primary"
                                            className="bg-gray-400"
                                            loading={inProgress}
                                            onClick={() => onNominateSurvivor(dataObjectList[i], "MERGE_DUPLICATES")}
                                        >
                                            Nominate
                                        </Button>
                                    )}
                                </Tooltip>
                            )}
                            {dataObjectList[i]._survivor && dataObjectList[i]._deduplicationOption === "MERGE_DUPLICATES" && (
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                label: "Preview record merge",
                                                key: "PREVIEW_RECORD_MERGE",
                                                disabled: dataObjectList[i]._nominationResult === "REMOVE_IN_PROGRESS",
                                                icon: (
                                                    <ProfileTwoTone
                                                        disabled={dataObjectList[i]._nominationResult === "REMOVE_IN_PROGRESS"}
                                                        style={{ color: "yellow", cursor: "pointer" }}
                                                    />
                                                ),
                                                onClick: (info) => {
                                                    info.domEvent?.stopPropagation && info.domEvent.stopPropagation();
                                                    info.domEvent?.preventDefault && info.domEvent.preventDefault();
                                                    onPreviewClick(dataObjectList[i]);
                                                },
                                            },
                                            {
                                                label: "Merge rules",
                                                key: "MERGE_RULES",
                                                disabled: dataObjectList[i]._nominationResult === "REMOVE_IN_PROGRESS",
                                                icon: <MergeCellsOutlined />,
                                                onClick: (info) => {
                                                    info.domEvent?.stopPropagation && info.domEvent.stopPropagation();
                                                    info.domEvent?.preventDefault && info.domEvent.preventDefault();
                                                    onMergeRulesClick(
                                                        dataObjectList[i],
                                                        dataObjectList.filter((item) => item._groupId === dataObjectList[i]._groupId),
                                                    );
                                                },
                                            },
                                        ],
                                    }}
                                >
                                    <Button
                                        icon={<MergeOutlined />}
                                        size="small"
                                        type="dashed"
                                        onClick={(e) => {
                                            e.stopPropagation && e.stopPropagation();
                                            e.preventDefault && e.preventDefault();
                                        }}
                                    >
                                        Merge Rules
                                    </Button>
                                </Dropdown>
                            )}
                            {!dataObjectList[i]._survivor && dataObjectList[i]._deduplicationOption !== "DELETE_DUPLICATES" && (
                                <div className="h-full w-0 border-r border-dotted border-gray-200" />
                            )}
                            {dataObjectList[i]._deduplicationOption === "DELETE_DUPLICATES" && (
                                <Tooltip placement="bottomLeft" title="Click to remove deletion">
                                    <Button
                                        icon={<DeleteFilled />}
                                        type="primary"
                                        size="small"
                                        danger
                                        loading={inProgress}
                                        onClick={() => onRemoveSurvivorNomination(dataObjectList[i])}
                                    />
                                </Tooltip>
                            )}
                            {!dataObjectList[i]._survivor && dataObjectList[i]._deduplicationOption !== "MERGE_DUPLICATES" && (
                                <Tooltip placement="bottomLeft" title="Delete other duplicates">
                                    <Button
                                        icon={<DeleteFilled />}
                                        type="default"
                                        size="small"
                                        danger
                                        loading={inProgress}
                                        disabled={dataObjectList[i]._deduplicationOption === "MERGE_DUPLICATES"}
                                        onClick={() => onNominateSurvivor(dataObjectList[i], "DELETE_DUPLICATES")}
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </GridCell>,
            );
            recordIds.push(
                <GridCell key={`recordId-${i}`} index={i} title={dataObjectList[i]._dataObjectId} type={actionType} gold={gold}>
                    <Tooltip placement="bottomLeft" title={dataObjectList[i]._dataObjectId}>
                        <p className="whitespace-nowrap text-nowrap">{dataObjectList[i]._dataObjectId}</p>
                    </Tooltip>
                </GridCell>,
            );
            businessAreaNames.push(
                <GridCell key={`businessArea-${i}`} index={i} type={actionType} gold={gold}>
                    {dataObjectList[i]._businessAreaName}
                </GridCell>,
            );
            schemaNames.push(
                <GridCell key={`schemaName-${i}`} index={i} type={actionType} gold={gold}>
                    {dataObjectList[i]._schemaName}
                </GridCell>,
            );
            dataSetNames.push(
                <GridCell key={`dataset-${i}`} index={i} type={actionType} gold={gold}>
                    <span className="inline-flex items-center gap-1">
                        {gold && <CrownFilled className="text-amber-500" />}
                        {dataObjectList[i]._dataSetName}
                    </span>
                </GridCell>,
            );

            // Loop through all tags and put into respective group
            dataTags.forEach((label) => {
                if (dataObjectList[i]._tags && dataObjectList[i]._tags[label] && dataObjectList[i]._tags[label].length > 0) {
                    taggings[label].push(
                        <GridCell index={i} className="duplicate-dataobject-tag-detail" key={`${dataObjectList[i]._dataObjectId}-tags${label}`} type={actionType} gold={gold}>
                            {dataObjectList[i]._tags[label].map((t, tagIndex) => (
                                <div key={`${dataObjectList[i]._dataObjectId}-tags${label}-tag${tagIndex}}`}>
                                    <p className="text-xs font-bold text-gray-400">{t.name}:</p>
                                    <p>{t.value}</p>
                                </div>
                            ))}
                        </GridCell>,
                    );
                } else {
                    taggings[label].push(
                        <GridCell index={i} className="italic text-gray-400" key={`${dataObjectList[i]._dataObjectId}-tags${label}`} type={actionType} gold={gold}>
                            No tag found
                        </GridCell>,
                    );
                }
            });

            recordFields.push(
                <GridCell index={i} key={`${dataObjectList[i]._dataObjectId}-fields`} className="border-b" gold={gold}>
                    <DataObjectFieldsPreview
                        tenantId={dataObjectList[i]._tenantId}
                        businessAreaId={dataObjectList[i]._businessAreaId}
                        schemaId={dataObjectList[i]._schemaId}
                        dataSetId={dataObjectList[i]._dataSetId}
                        dataObjectId={dataObjectList[i]._dataObjectId}
                    />
                </GridCell>,
            );
        }

        const combined: React.ReactNode[] = [];

        // Survivor options
        if (survivorOptions.length > 0) {
            combined.push(...survivorOptions);
        }

        // Record IDs
        if (recordIds.length > 0) {
            combined.push(<GridHeading key="recordId">Record ID</GridHeading>);
            combined.push(...recordIds);
        }

        // Business Areas
        if (businessAreaNames.length > 0) {
            combined.push(<GridHeading key="businessArea">Business Area</GridHeading>);
            combined.push(...businessAreaNames);
        }

        // Schema Names
        if (schemaNames.length > 0) {
            combined.push(<GridHeading key="schema">Schema</GridHeading>);
            combined.push(...schemaNames);
        }

        // DataSet Names
        if (dataSetNames.length > 0) {
            combined.push(<GridHeading key="dataset">DataSet</GridHeading>);
            combined.push(...dataSetNames);
        }

        dataTags.forEach((t) => {
            if (taggings[t].length > 0) {
                combined.push(<GridHeading key={t}>{t}</GridHeading>);
                combined.push(...taggings[t]);
            }
        });

        // Record Fields
        if (recordFields.length > 0) {
            combined.push(<GridHeading key="record-fields">Schema Fields</GridHeading>);
            combined.push(...recordFields);
        }

        setAlignedGrid(combined);
    }, [dataObjectList, dataTags, isGold, hasGolden, inProgress, onMergeRulesClick, onNominateSurvivor, onPreviewClick, onRemoveSurvivorNomination, tab]);

    return (
        <FlexContainerLayout
            headerClassName="flex gap-3 rounded-md border border-slate-100 bg-slate-50 p-4"
            header={
                <>
                    <Radio.Group value={tab} onChange={(e) => setTab(e.target.value)} optionType="button">
                        <Radio value="all">
                            <div className="flex items-center justify-center gap-2">
                                <span>All</span>
                                <Badge count={dataObjectList.length} showZero size="small" color="purple" />
                            </div>
                        </Radio>
                        <Radio value="merge">
                            <div className="flex items-center justify-center gap-2">
                                <span>To Merge</span>
                                <Badge count={mergeCount} showZero size="small" color="green" />
                            </div>
                        </Radio>
                        <Radio value="delete">
                            <div className="flex items-center justify-center gap-2">
                                <span>To Delete</span>
                                <Badge count={deleteCount} showZero size="small" />
                            </div>
                        </Radio>
                    </Radio.Group>
                    {hasGolden && (
                        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs" data-testid="survivorship-key">
                            <span className="inline-flex items-center gap-1 font-semibold text-amber-800">
                                <CrownFilled className="text-amber-500" />
                                Survivorship Key:
                            </span>
                            <GoldenNominalBadge label="Golden Nominal — record persists" />
                            <GoldenNominalBadge tone="subordinate" label="Subordinate — merged into Golden Nominal" />
                            <span className="text-gray-500">The Golden Nominal dataset is set per schema in Dataset edit mode.</span>
                        </div>
                    )}
                    {stewardBannerVisible && (
                        <div
                            className="flex flex-wrap items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs"
                            data-testid="steward-review-banner"
                        >
                            <Tag color="orange" className="m-0">
                                <AuditOutlined /> Needs steward review
                            </Tag>
                            <span className="text-gray-600">
                                {(stewardFlagged.__stewardReviewFields || []).map((field) => field.fieldName).join(", ") || "Unresolved field conflicts"}
                            </span>
                            <Button size="small" type="primary" onClick={() => setResolveOpen(true)} data-testid="steward-resolve-open">
                                Resolve conflicts
                            </Button>
                        </div>
                    )}
                    <StewardResolveModal
                        open={resolveOpen}
                        onClose={() => setResolveOpen(false)}
                        survivor={stewardFlagged}
                        onResolved={() => setResolvedLocally(stewardFlagged ? stewardFlagged._dataObjectId : null)}
                    />
                    <SuridDetailModal open={!!suridOpen} onClose={() => setSuridOpen(null)} surId={suridOpen} />
                </>
            }
        >
            {alignedGrid.length > 0 ? (
                <>
                    <div className="grid" style={{ gridTemplateColumns }}>
                        {alignedGrid}
                    </div>
                    {hasGolden && (
                        <div className="mb-4 mt-2 flex items-center gap-2 text-xs text-amber-700" data-testid="survivorship-footer">
                            <CrownFilled className="text-amber-500" />
                            Golden Nominal record will be preserved; subordinate record(s) merged into it.
                        </div>
                    )}
                </>
            ) : (
                <div className="flex flex-1 items-center justify-center border border-dashed border-gray-100">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No record" />
                </div>
            )}
        </FlexContainerLayout>
    );
}
